<template>
  <v-card class="accounts">

    <div class="d-flex justify-start align-center">
      <v-card-title>Accounts</v-card-title>
      
      <v-chip-group column>
      
        <template v-if="getCreditControlAccountsConfig.payment_method">
          <v-chip small close @click:close="removeFilter('payment_method')">
            <span><b>Payment Method: </b>{{ paymentMethodChip }}</span>
          </v-chip>
        </template>

        <template v-if="getCreditControlAccountsConfig.last_dd_failed_reason">
          <v-chip small close @click:close="removeFilter('last_dd_failed_reason')">
            <span><b>Last DD Failed Reason: </b>{{ lastDDFailedReasonChip }}</span>
          </v-chip>
        </template>

        <template v-if="getCreditControlAccountsConfig.most_recent_payment_date.length">
          <v-chip small close @click:close="removeFilter('most_recent_payment_date')">
            <span><b>Last Payment: </b>{{ getCreditControlAccountsConfig.most_recent_payment_date.join(' to ') }}</span>
          </v-chip>
        </template>

        <template v-if="getCreditControlAccountsConfig.status.length">
          <v-chip small close @click:close="removeFilter('status')">
            <span><b>Status: </b>{{ getCreditControlAccountsConfig.status.join(", ") }}</span>
          </v-chip>
        </template>

        <template v-if="getCreditControlAccountsConfig.status_reason.length">
          <v-chip small close @click:close="removeFilter('status_reason')">
            <span><b>Status Reason: </b>{{ getCreditControlAccountsConfig.status_reason.join(", ") }}</span>
          </v-chip>
        </template>

        <template v-if="getCreditControlAccountsConfig.exclusion_kind.length">
          <v-chip small close @click:close="removeFilter('exclusion_kind')">
            <span><b>Exclusion Kind: </b>{{ exclusionKindChip }}</span>
          </v-chip>
        </template>

      </v-chip-group>

      <div class="d-flex justify-self-end ml-auto mr-4">
        <v-tooltip top :disabled="hasUserGotRoles(['can-download-reports']) && isFilterActive" v-bind="$attrs">
          <template v-slot:activator="{ on, attrs }">
            <div v-bind="attrs" v-on="on">
              <v-btn color="secondary" small :disabled="!(hasUserGotRoles(['can-download-reports']) && isFilterActive)" class="mr-2" @click="exportToCSV">Download</v-btn>
            </div>
          </template>
          <span v-if="!hasUserGotRoles(['can-download-reports'])">
            You do not have the permission to perform this action
          </span>
          <span v-else-if="!isFilterActive">
            Select at least one status or exclusion kind
          </span>
        </v-tooltip>

        <v-tooltip top :disabled="hasUserGotRoles(['can-download-reports']) && isFilterActive" v-bind="$attrs">
          <template v-slot:activator="{ on, attrs }">
            <div v-bind="attrs" v-on="on">
              <v-btn color="secondary" small :disabled="!(hasUserGotRoles(['can-send-to-dialer']) && isFilterActive)" @click="sendToDialer">Send to dialer</v-btn>
            </div>
          </template>
          <span v-if="!hasUserGotRoles(['can-send-to-dialer'])">
            You do not have the permission to perform this action
          </span>
          <span v-else-if="!isFilterActive">
            Select at least one status or exclusion kind
          </span>
        </v-tooltip>
      </div>
    </div>

    <v-card-text>
      <v-data-table :headers="headers" :items="getCreditControlAccounts" :loading="getFetchCreditControlAccountsLoading" :options.sync="options" :page.sync="getCreditControlAccountsConfig.page" :server-items-length="getCreditControlAccountsConfig.paginationCount" :footer-props="{itemsPerPageOptions}" @click:row="goToCreditControl" class="body--text accounts__table">
        
        <template v-slot:item.customer_id="{ item }">
          <a @click.stop="goToBillingAccount(item)">{{ item.customer_id }}</a>
        </template>

        <template v-slot:item.most_recent_payment_date="{ item }">
          {{ item.most_recent_payment_date | formatCustomDate('YYYY-MM-DD') }}
        </template>

        <template v-slot:item.balance="{ item }">
          {{ item.balance | formatCurrency }}
        </template>

        <template v-slot:item.quick_note="{ item }">
          <v-btn fab depressed color="transparent" x-small @click.stop="editQuickNote(item)" class="mr-1">
            <v-icon color="grey">{{ mdiPencil }}</v-icon>
          </v-btn>

          {{ item.quick_note }}
        </template>

        <template v-slot:item.action="{ item }">
          <v-btn fab depressed color="primary" x-small @click.stop="goToBillingAccount(item)" class="mr-1">
            <v-icon>{{ mdiAccount }}</v-icon>
          </v-btn>

          <v-btn icon color="primary" x-small @click.stop="goToLedger(item)">
            <v-icon>{{ mdiBookOpenBlankVariant }}</v-icon>
          </v-btn>
        </template>

      </v-data-table>
    </v-card-text>

    <QuickNoteDialog v-model="quickNoteDialog" :account_id="note.account_id" :quick_note="note.quick_note" />
  </v-card>
</template>

<script>
  import { mapMutations, mapActions, mapGetters } from 'vuex';
  
  import { mdiAccount, mdiBookOpenBlankVariant, mdiPencil } from '@mdi/js';

  import QuickNoteDialog from '../QuickNoteDialog.vue';

  export default {
    components: {
      QuickNoteDialog
    },

    data() {
      return {
        mdiAccount,
        mdiBookOpenBlankVariant,
        mdiPencil,
        ledgerLink: process.env.VUE_APP_LEDGER_LINK || `https://ledger-test-bonline.appspot.com/`,

        headers: [
          { text: 'Account ID', sortable: false, value: 'customer_id' },
          { text: 'Payment Method', sortable: false, value: 'payment_method' },
          { text: 'Last Payment', sortable: false, value: 'most_recent_payment_date' },
          { text: 'Balance', sortable: false, value: 'balance' },
          { text: 'Counter', sortable: false, value: 'counter' },
          { text: 'Days in Debt', sortable: false, value: 'debt_days' },
          { text: 'Products', sortable: false, value: 'products' },
          { text: 'Exclusion Kind', sortable: false, value: 'exclusion_kind' },
          { text: 'Status', sortable: false, value: 'status' },
          { text: 'Status Reason', sortable: false, value: 'status_reason' },
          { text: 'Last UDD', sortable: false, value: 'last_dd_failed_reason' },
          { text: 'Quick Note', sortable: false, value: 'quick_note'},
          { text: '', sortable: false, value: 'action' },
        ],   
        options: {
          itemsPerPage: 50,
        },
        itemsPerPageOptions: [5,10,15,25,50],
        
        quickNoteDialog: false,
        note: {
          account_id: "",
          quick_note: ""
        }
      }
    },

    computed: {
      ...mapGetters("globals", ["hasUserGotRoles"]),
      ...mapGetters('creditControlDashboard', ['getCreditControlAccounts', 'getFetchCreditControlAccountsLoading', 'getCreditControlAccountsConfig', 'getCreditControlAccountsFilters']),

      paymentMethodChip() {
        return this.getCreditControlAccountsFilters['paymentMethods'].find(paymentMethod => paymentMethod.value === this.getCreditControlAccountsConfig.payment_method).text;
      },

      lastDDFailedReasonChip() {
        return this.getCreditControlAccountsFilters['lastDDFailedReasons'].find(reason => reason.value === this.getCreditControlAccountsConfig.last_dd_failed_reason).text;
      },

      exclusionKindChip() {
        return this.getCreditControlAccountsConfig.exclusion_kind.map(exlusionKind => {
          return this.getCreditControlAccountsFilters['statusMetrics'].find(exlusion => exlusionKind === exlusion.value).text;
        }).join(", ");
      },

      isFilterActive() {
        return [...this.getCreditControlAccountsConfig.status, ...this.getCreditControlAccountsConfig.exclusion_kind].length ? true : false;
      },
    },

    watch: {
      options: {
        immediate: false,
        handler() {
          this.updateCreditControlAccountsPagination(this.options);
        },
        deep: true,
      },

      quickNoteDialog: {
        handler(val) {
          if(!val) {
            this.note.account_id = "";
            this.note.quick_note = "";
          }
        }
      }
    },

    beforeDestroy() {
      this.RESET_CREDIT_CONTROL_DASHBOARD_STORE();
    },

    methods: {
      ...mapMutations('creditControlDashboard', ['RESET_CREDIT_CONTROL_DASHBOARD_STORE']),
      ...mapActions('creditControlDashboard', ['updateCreditControlAccountsPagination', 'removeCreditControlAccountsFilter', 'exportToCSV', 'sendToDialer']),

      goToCreditControl(item) {
        this.$router.push(`/accounts/${item.customer_id}/credit-control`);
      },

      goToBillingAccount(item) {
        window.open(`/accounts/${item.customer_id}/`);
      },

      goToLedger(item) {
        window.open(`${this.ledgerLink}transactions/${item.customer_id}`);
      },

      removeFilter(filter) {
        this.removeCreditControlAccountsFilter(filter);
      },

      editQuickNote(item) {
        this.quickNoteDialog = true;
        this.note.account_id = item.customer_id;
        this.note.quick_note = item.quick_note ? item.quick_note : "";
      },
    }
  }
</script>

<style lang="scss" scoped>
  .accounts__table::v-deep tbody tr {
    cursor: pointer !important;
  }
</style>